import Accordion from '@/common/components/Accordion'
import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessage } from '@/common/components/ErrorMessage'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { MultipleImageUploader } from '@/common/components/MultipleImageUploader'
import SelectController from '@/common/components/SelectController'
import TextArea from '@/common/components/TextArea'
import useBlobs from '@/common/hooks/queries/useBlobs'
import useDebounce from '@/common/hooks/useDebounce'
import { useGoBack } from '@/common/hooks/useGoBack'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { Mapper } from '@/common/services/mapper'
import { Option } from '@/common/types/option'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useEquipments } from '@/features/equipments/hooks/useEquipments'
import { EquipmentsTransformer } from '@/features/equipments/services/equipmentTransformer'
import { MaintenanceSolicitationObjectives } from '@/features/maintenanceSolicitations/components/MaintenanceSolicitationObjectives'
import { ModalAddMaintenanceObjective } from '@/features/maintenanceSolicitations/components/ModalAddMaintenanceObjective'
import { solicitationKindOptions } from '@/features/maintenanceSolicitations/constants/solicitationKind.constant'
import { useCreateMaintenanceSolicitation } from '@/features/maintenanceSolicitations/hooks/useCreateMaintenanceSolicitation'
import {
  maintenanceSolicitationCreateSchema,
  maintenanceSolicitationObjectiveSchema,
  MaintenanceSolicitationObjectiveValidationSchema,
  MaintenanceSolicitationValidationSchema,
} from '@/features/maintenanceSolicitations/schemas/create.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { QueryClient } from '@tanstack/react-query'
import { ChevronsRightIcon, InfoIcon } from 'lucide-react'
import { useState } from 'react'
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

const Create = () => {
  const { id } = useParams()
  const goBack = useGoBack()
  const navigate = useNavigate()
  const queryClient = new QueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchEquipment, setSearchEquipment] = useDebounce('')

  const { data: equipmentOptions } = useEquipments({
    filters: { companyId: id, status: 'enabled', search: searchEquipment },
    mapper: Mapper.mapWithTransformer({ transformer: EquipmentsTransformer.optionsTransformer() }),
  })

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs()

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()

  const userHasPermissionToCreateMaintenanceSolicitation =
    isUserPermissionsPending || hasUserPermission('Client::Maintenance::Solicitations::Create')

  const formMethods = useForm<MaintenanceSolicitationValidationSchema>({
    resolver: zodResolver(maintenanceSolicitationCreateSchema),
  })

  const {
    control,
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = formMethods

  const {
    fields: objectivesFields,
    append: appendObjective,
    remove: removeObjective,
  } = useFieldArray({
    control,
    name: 'objectives',
  })

  const { mutate: createMaintenanceSolicitation, isPending: createMaintenanceSolicitationIsPending } =
    useCreateMaintenanceSolicitation({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['maintenanceSolicitations'] })
        navigate('/maintenance-solicitations')
      },
      onError: FormTransformers.errorsTransformer({ setError, setMessages }),
    })

  const onSubmit: SubmitHandler<MaintenanceSolicitationValidationSchema> = (data) => createMaintenanceSolicitation(data)

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={userHasPermissionToCreateMaintenanceSolicitation ? 200 : 403}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="false" className="flex flex-col gap-y-6">
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <GoBackButton fallback="/maintenance-solicitations" searchLastRouteFromFallback />

              <p>Solicitações</p>

              <ChevronsRightIcon className="text-white-400" />

              <h2>Nova Solicitação</h2>
            </div>

            <div className="flex gap-4">
              <Button variant="outline-error" onClick={() => goBack({ fallback: '/maintenance-solicitations' })}>
                Cancelar
              </Button>

              <Button
                type="submit"
                name="create_maintenance_solicitation"
                className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
                disabled={createMaintenanceSolicitationIsPending || uploadBlobIsPending}
                isLoading={createMaintenanceSolicitationIsPending}
              >
                Criar Solicitação
              </Button>
            </div>
          </div>

          <Box className="max-h-[calc(100vh-128px)] flex flex-col gap-4 overflow-y-auto">
            <p className="text-lg font-bold">Descrição da solicitação</p>

            <div className="flex gap-x-2">
              <InfoIcon strokeWidth={1} className="size-7 text-warning" />

              <div>
                <p className="text-xs font-bold">Avalie o cockpit de manutenção do equipamento.</p>

                <p className="text-xs font-light italic">
                  Considere antecipar Programações de manutenção deste equipamento para unificar em um único Processo de
                  manutenção.
                </p>
              </div>
            </div>

            <MultipleImageUploader
              label={
                <p className="max-w-24 text-xs font-bold text-center text-white-800">Anexe fotos do equipamento</p>
              }
              name="photos_attachments"
              inputClassName="bg-white-100 border-none"
              iconClassName="text-white-800"
              accept="image/jpeg, image/png"
              control={control}
              uploadBlob={uploadBlob}
              fieldName="photos_attachments"
              disabled={uploadBlobIsPending || createMaintenanceSolicitationIsPending}
              errorMessage={errors.photos_attachments?.message}
            />

            <div className="flex w-full gap-x-2">
              <SelectController
                control={control}
                name="equipment_id"
                label="Equipamento"
                markAsRequired
                className="w-full"
                options={(equipmentOptions as Option[]) || []}
                onInputChange={setSearchEquipment}
                isDisabled={createMaintenanceSolicitationIsPending}
                errorMessage={errors.equipment_id?.message}
              />

              <SelectController
                control={control}
                name="kind"
                label="Tipo de solicitação"
                markAsRequired
                className="w-full"
                options={solicitationKindOptions || []}
                onInputChange={setSearchEquipment}
                isDisabled={createMaintenanceSolicitationIsPending}
                errorMessage={errors.kind?.message}
              />
            </div>

            <TextArea
              label="Descritivo da solicitação"
              {...register('observations')}
              markAsRequired
              disabled={createMaintenanceSolicitationIsPending}
              errorMessage={errors.observations?.message}
            />

            <Accordion
              defaultOpen
              className="py-1"
              head={
                <div className="flex w-full flex-col items-start gap-y-1">
                  <p>
                    Objetos de manutenção <span className="text-danger-soft"> *</span>
                  </p>

                  {!!errors.objectives?.message && (
                    <ErrorMessage name="objectives" message={errors.objectives?.message} />
                  )}
                </div>
              }
              body={
                <div className="grid grid-cols-1 md:grid-cols-2 items-stretch justify-stretch gap-2">
                  <ModalAddMaintenanceObjective<MaintenanceSolicitationObjectiveValidationSchema>
                    validationSchema={maintenanceSolicitationObjectiveSchema}
                    objectivesFields={objectivesFields}
                    appendObjective={appendObjective}
                    equipmentId={watch('equipment_id')?.value}
                    disabled={createMaintenanceSolicitationIsPending}
                  />

                  <MaintenanceSolicitationObjectives
                    objectivesFields={objectivesFields}
                    removeObjective={removeObjective}
                  />
                </div>
              }
            />
          </Box>

          {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
        </form>
      </FormProvider>
    </ErrorMessageWrapper>
  )
}

export default Create
